import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { confirmStaffingBackfill, previewStaffingBackfill } from '../../api/client'
import Modal from '../Modal'
import { isoWeekNumber } from './weeks'

function weekLabel(iso) {
  const d = new Date(iso + 'T00:00:00')
  return `S${isoWeekNumber(iso)} · ${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`
}

/**
 * Copies whatever Plan Week already logged onto weeks the plan never covered
 * at the time -- for a month the project only started tracking after the
 * fact, so its hh-presupuesto counts what actually happened instead of
 * reading as if nobody worked it.
 *
 * One step, not three like the Excel import: there is nothing to upload, the
 * source is Plan Week's own numbers, already sitting in the project. Preview,
 * then apply.
 */
export default function BackfillModal({ projectId, onClose, onApplied }) {
  const { t } = useTranslation(['engineering', 'common'])
  const [preview, setPreview] = useState(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  const [applied, setApplied] = useState(false)

  const k = (suffix, opts) => t(`staffing.backfill.${suffix}`, opts)

  useEffect(() => {
    previewStaffingBackfill(projectId)
      .then(setPreview)
      .catch(err => setError(err.response?.data?.detail || t('staffing.backfill.failed')))
  }, [projectId, t])

  const handleConfirm = async () => {
    setBusy(true)
    setError('')
    try {
      const result = await confirmStaffingBackfill(projectId)
      setApplied(true)
      onApplied?.(result.staffing)
    } catch (err) {
      setError(err.response?.data?.detail || k('failed'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal title={k('title')} onClose={onClose}>
      <div className="space-y-4" style={{ minWidth: 'min(640px, 80vw)' }}>
        <p className="text-sm text-gray-600">{k('intro')}</p>

        {error && <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">{error}</p>}

        {!preview && !error && (
          <p className="text-sm text-gray-400 py-6 text-center">{k('loading')}</p>
        )}

        {preview && !applied && (
          <>
            {preview.changes.length === 0 ? (
              <p className="text-sm text-gray-400 py-6 text-center">{k('nothingToDo')}</p>
            ) : (
              <>
                <div className="flex gap-2">
                  <div className="flex-1 rounded-lg border border-gray-200 px-3 py-2">
                    <div className="text-xl font-semibold tabular-nums text-gray-900">
                      {preview.weeks_changed}
                    </div>
                    <div className="text-[11px] text-gray-500">{k('weeksChanged')}</div>
                  </div>
                  <div className="flex-1 rounded-lg border border-gray-200 px-3 py-2">
                    <div className="text-xl font-semibold tabular-nums text-emerald-700">
                      +{preview.hours_added}
                    </div>
                    <div className="text-[11px] text-gray-500">{k('hoursAdded')}</div>
                  </div>
                  {preview.trimmed > 0 && (
                    <div className="flex-1 rounded-lg border border-gray-200 px-3 py-2">
                      <div className="text-xl font-semibold tabular-nums text-amber-700">
                        {preview.trimmed}
                      </div>
                      <div className="text-[11px] text-gray-500">{k('trimmedCount')}</div>
                    </div>
                  )}
                </div>

                {preview.window_start && (
                  <p className="text-xs text-gray-500 bg-gray-50 px-3 py-2 rounded-lg">
                    {k('windowWarning', { date: weekLabel(preview.window_start) })}
                  </p>
                )}

                {preview.trimmed > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 px-3 py-2 rounded-lg">
                    {k('trimmedWarning')}
                  </p>
                )}

                <div className="max-h-72 overflow-auto rounded-lg border border-gray-200">
                  <table className="w-full text-xs">
                    <tbody>
                      {preview.changes.map((c, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0">
                          <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap">{c.name}</td>
                          <td className="px-2 py-1.5 text-gray-400 whitespace-nowrap">
                            {weekLabel(c.week)}
                          </td>
                          <td className="px-2 py-1.5 text-gray-400 tabular-nums whitespace-nowrap">
                            {c.from_hours} → <span className="text-gray-900 font-medium">{c.to_hours}</span> h
                          </td>
                          <td className="px-2 py-1.5 text-amber-700">
                            {c.trimmed && k('trimmedRow')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <div className="flex gap-3 pt-1">
              {preview.changes.length > 0 && (
                <button
                  onClick={handleConfirm}
                  disabled={busy}
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-40"
                >
                  {busy ? k('applying') : k('confirm')}
                </button>
              )}
              <button
                onClick={onClose}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg text-sm font-medium hover:bg-gray-200"
              >
                {t('common:actions.cancel')}
              </button>
            </div>
          </>
        )}

        {applied && (
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              {k('doneMessage', { count: preview.weeks_changed, hours: preview.hours_added })}
            </p>
            <button
              onClick={onClose}
              className="w-full bg-gray-900 text-white py-2 rounded-lg text-sm font-medium hover:bg-gray-800"
            >
              {t('staffing.import.close')}
            </button>
          </div>
        )}
      </div>
    </Modal>
  )
}
